import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  ButtonGroup,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { FormContext } from "../context/FormContext";
import { ToolState, ToolContext } from "../context/ToolContext";
import * as layout from "../constants/layout";
import { useSelector } from "react-redux";
import { ToolbarParentMenu, Toolbar } from "../model/internal/toolbar";
import { AssetStyleInfo } from "../model/internal/assetInfo";
import AssetIcon from "./assets/asset-icon/AssetIcon";
import { getSelectedMenuItem } from "../utils/toolbarFunctions";
import { RootState } from "../app/store";
import { checkReduxNetworkPopulated } from "../app/networkSlice";

interface IconDropDownMenuProps {
  toolbar: Toolbar;
  selectedItem: number[];
  activeCategory: number;
  selectItem: (categoryIndex: number, item?: AssetStyleInfo, itemIndex?: number) => void;
  undo: () => void;
  redo: () => void;
}

const IconDropDownMenu = ({
  toolbar,
  selectedItem,
  activeCategory,
  selectItem,
  undo,
  redo,
}: IconDropDownMenuProps) => {
  const { formState } = useContext(FormContext);
  const { toolState, setToolState } = useContext(ToolContext);
  const { clientSettings } = formState;
  const { showResults, assessmentFailed } = toolState;
  const [dropdownOpen, setOpen] = useState<boolean[]>([...toolbar.map(() => false)]);
  const [navbarHeight, setNavbarHeight] = useState<number>(layout.NAVBAR_HEIGHT);

  const past = useSelector((state: RootState) => state.network.past);
  const future = useSelector((state: RootState) => state.network.future);
  const popupResults = useSelector((state: RootState) => state.settings.isPopupResultsEnabled);
  const isReduxNetworkPopulated = useSelector((state: RootState) =>
    checkReduxNetworkPopulated(state),
  );

  const handleWindowSizeChange = () => {
    const navbar = document.getElementById("ea_navbar") as any;
    setNavbarHeight(navbar.clientHeight);
  };
  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    // If the currently selected tool is disabled, revert to the first tool in the category
    toolbar.forEach((category: ToolbarParentMenu, categoryIndex: number) => {
      const item: AssetStyleInfo = getSelectedMenuItem(category, selectedItem[categoryIndex]);
      if (!toolIsEnabled(item.class)) {
        if (categoryIndex === activeCategory) {
          selectItem(categoryIndex, category.items[0], 0);
        } else {
          const _toolState: ToolState = { ...toolState };
          _toolState.selectedTool[categoryIndex] = 0;
          setToolState(_toolState);
        }
      }
    });
  }, [showResults, assessmentFailed, activeCategory, JSON.stringify(selectedItem)]);

  const dropdownToggle = (categoryIndex: number): void => {
    let _dropdownOpen = [...dropdownOpen];
    _dropdownOpen[categoryIndex] = !dropdownOpen[categoryIndex];
    setOpen(_dropdownOpen);
  };

  const toolIsEnabled = (toolClass: string): boolean => {
    if (toolClass === "pipette") {
      return isReduxNetworkPopulated;
    }
    if (toolClass === "select-results") {
      return popupResults && showResults && !assessmentFailed;
    }
    return true;
  };

  const gotFeature = (requiredFeature: string | undefined | null): boolean => {
    return !requiredFeature || clientSettings.features[requiredFeature];
  };

  return (
    <div className="mb-2" style={{ marginTop: navbarHeight - layout.NAVBAR_HEIGHT }}>
      <ButtonGroup vertical>
        {toolbar.map((category: ToolbarParentMenu, categoryIndex: number) => (
          <React.Fragment key={categoryIndex}>
            {gotFeature(category.requiredFeature) && !category.topLevelItem && (
              <ButtonDropdown
                key={`buttondropdown-${categoryIndex}`}
                isOpen={dropdownOpen[categoryIndex]}
                toggle={() => dropdownToggle(categoryIndex)}
                direction="right"
              >
                <Button
                  id={`icon${category.items[selectedItem[categoryIndex]].id}`}
                  active={activeCategory === categoryIndex}
                  color="dark"
                  onClick={() => selectItem(categoryIndex)}
                >
                  <AssetIcon assetStyle={category.items[selectedItem[categoryIndex]]} />
                </Button>
                <DropdownToggle
                  split
                  color="dark"
                  id={`toggle${category.items[selectedItem[categoryIndex]].type}`}
                />
                <DropdownMenu color="dark">
                  {category.items
                    .filter((p: AssetStyleInfo) => gotFeature(p.requiredFeature))
                    .map((item: AssetStyleInfo, itemIndex: number) => (
                      <DropdownItem
                        id={`item${item.id}`}
                        key={itemIndex}
                        tag="a"
                        disabled={!toolIsEnabled(item.class)}
                        onClick={() => selectItem(categoryIndex, item, itemIndex)}
                      >
                        <AssetIcon assetStyle={item} /> {item.name}
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </ButtonDropdown>
            )}
            {gotFeature(category.requiredFeature) && !!category.topLevelItem && (
              <ButtonDropdown className="horizontal-buttons">
                <Button
                  id={`item${category.topLevelItem.id}`}
                  className="pt-1"
                  active={activeCategory === categoryIndex}
                  color="dark"
                  title={category.topLevelItem.name}
                  disabled={!toolIsEnabled(category.topLevelItem.class)}
                  onClick={() => selectItem(categoryIndex, category.topLevelItem, -1)}
                >
                  <AssetIcon assetStyle={category.topLevelItem} />
                </Button>
              </ButtonDropdown>
            )}
          </React.Fragment>
        ))}
        {clientSettings.features.HistoryModeEnabled && (
          <ButtonDropdown className="horizontal-buttons">
            <Button
              id="undo-button"
              className="pt-1"
              color="dark"
              title="Undo"
              disabled={past.length === 0}
              onClick={undo}
            >
              <i className={"icon-undo-solid"}></i>
            </Button>
            <Button
              id="redo-button"
              className="pt-1"
              color="dark"
              title="Redo"
              disabled={future.length === 0}
              onClick={redo}
            >
              <i className={"icon-redo-solid"}></i>
            </Button>
          </ButtonDropdown>
        )}
      </ButtonGroup>
    </div>
  );
};

export default IconDropDownMenu;
